import React, { forwardRef, ReactElement, useEffect, useState } from 'react';

import { DropdownContainer } from '../dropdown';
import { FilterRangePopupProps } from './filterRangePopup.model';
import * as S from './filterRangePopup.styled';
import { PopupFooterContainer } from './popupFooter';
import { TextControlComponent } from '../form';
import { InputColors } from '../../styles';
import { useTranslations } from '../translations';

export const FilterRangePopupContainer = forwardRef<
  HTMLElement,
  FilterRangePopupProps
>(
  (
    {
      open,
      resetButtonTitle,
      applyButtonTitle,
      onApply,
      onClose,
      onReset,
      position,
      fromTitle,
      toTitle,
    },
    ref
  ): ReactElement => {
    const translations = useTranslations();
    const [from, setFrom] = useState<string>('');
    const [to, setTo] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<Record<string, string>>(
      {}
    );

    const handleApply = (): void => {
      onClose();
      onApply(from, to);
    };

    const handleReset = (): void => {
      setFrom('');
      setTo('');

      onReset && onReset();
      onClose();
    };

    useEffect(() => {
      const isFromInteger = !from || Number.isInteger(+from);
      const isToInteger = !to || Number.isInteger(+to);
      switch (true) {
        case !isFromInteger || !isToInteger: {
          setErrorMessage({
            ...(!isFromInteger
              ? { from: translations.input_value_should_be_digits }
              : null),
            ...(!isToInteger
              ? { to: translations.input_value_should_be_digits }
              : null),
          });
          break;
        }
        case !!to && +to < +from: {
          setErrorMessage({
            to: `Минимальное значение ${from}`,
          });
          break;
        }
        default:
          setErrorMessage({});
          break;
      }
    }, [from, to]);

    return (
      <S.FilterRangePopupContainer>
        {open && (
          <DropdownContainer
            ref={ref}
            handleOutsideClick={onClose}
            top={8}
            position={position}
            extraStyles={S.FilterRangePopupDropdown}
          >
            <>
              <S.FilterRangePopupBody>
                <TextControlComponent
                  isInteger
                  type="text"
                  name="from"
                  margin="0"
                  placeholder={fromTitle}
                  color={InputColors.grey}
                  floatingPlaceholder={false}
                  value={from}
                  onReset={() => setFrom('')}
                  onChange={(e) => {
                    setFrom(e.target.value);
                  }}
                  errorMessage={errorMessage.from}
                  touched={true}
                />
                <TextControlComponent
                  isInteger
                  type="text"
                  name="to"
                  margin="0"
                  placeholder={toTitle}
                  color={InputColors.grey}
                  floatingPlaceholder={false}
                  value={to}
                  min={from}
                  max="10000"
                  onReset={() => setTo('')}
                  onChange={(e) => {
                    setTo(e.target.value);
                  }}
                  touched={!!to}
                  errorMessage={errorMessage.to}
                />
              </S.FilterRangePopupBody>
              <PopupFooterContainer
                resetButtonTitle={resetButtonTitle}
                handleReset={handleReset}
                applyButtonTitle={applyButtonTitle}
                applyDisabled={!!Object.keys(errorMessage).length}
                handleApply={handleApply}
              />
            </>
          </DropdownContainer>
        )}
      </S.FilterRangePopupContainer>
    );
  }
);

FilterRangePopupContainer.displayName = 'FilterRangePopupContainer';
